import { useSnackbar } from "@sagecentral/core-ui";
import { useCentralApi, useCurrentUser } from "hooks/shared";
import { method } from "lodash";
import IWorkItemResponsibility from "models/work-items/IWorkItemResponsibility";
import { useEffect, useReducer, useState } from "react";

type FetchResponsibilitiesSignature = (workitemId: number, draft?: boolean) => Promise<IWorkItemResponsibility[] | null>;
type AddResponsibilitiesSignature = (workitemId: number, responsibilityId: number, draft?: boolean) => Promise<number | null>;

type UseWorkItemProductMasterResult = [
    FetchResponsibilitiesSignature,
    AddResponsibilitiesSignature,
    () => void,
    boolean
];

type UseWorkItemProductMasterSignature = (workItemId: number) => UseWorkItemProductMasterResult;

const useWorkItemResponsibilities: UseWorkItemProductMasterSignature = (workItemId) => {
    const [ loading, setLoading ] = useState<boolean>(false);
    const [ refreshState, refresh ] = useReducer(v => v + 1, 0);
    const { displaySnackbar } = useSnackbar();
    const user = useCurrentUser();
    const fetcher = useCentralApi(user);

    const searchResponsibilities: FetchResponsibilitiesSignature = async (workitemId: number, draft?: boolean) => {
        if (!user || !user.access_token) {
            return null;
        }
        if (workitemId === 0) {
            return null;
        }
    
        const controller = new AbortController();
        
        try {
            setLoading(true);
            const uri = draft
            ? `/api/v3/drafts/workitems/${workitemId}/responsibilities`
            : `/api/v3/workitems/${workitemId}/responsibilities`;

            const resp = await fetcher(uri, {
                signal: controller.signal
            }, async (r) => {
                if (r.status === 404) {
                    return true;
                }
    
                return false;
            });

         
            if (!resp.ok) {
                displaySnackbar('error', 'WorkItem Responsibilities failed fetching');
                return null;
            }
            

            const data = await resp.json();

            return data.resources  as IWorkItemResponsibility[] ;
        } catch {
            return null;
        } finally {
            setLoading(false);
        }
    };


    const addResponsibilities: AddResponsibilitiesSignature = async (workitemId: number, responsibilityId: number, draft?: boolean) => {
        if (!user || !user.access_token) {
            return null;
        }
    
        const controller = new AbortController();
        
        try {
            setLoading(true);
            const uri = draft
            ? `/api/v3/drafts/workitems/${workitemId}/responsibility/${responsibilityId}`
            : `/api/v3/workitems/${workitemId}/responsibility/${responsibilityId}`;
            const resp = await fetcher(uri, {
                signal: controller.signal,
                method: 'POST'
            }, async (r) => {
                if (r.status === 404) {
                    return true;
                }
    
                return false;
            });

         
            if (!resp.ok) {
                displaySnackbar('error', 'Responsibility failed to be added');
                return null;
            }

            displaySnackbar('success', 'Responsibility added successfully');
            
            const data = await resp.json() as number;

            return data;
        } catch {
            return null;
        } finally {
            setLoading(false);
        }
    };
    

    return [
        searchResponsibilities,
        addResponsibilities,
        () => refresh(),
        loading
    ];
};

export default useWorkItemResponsibilities;